import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, FileText, Video, Package, Link2, Users, Check, Library, Loader } from 'lucide-react';
import type { CourseSectionsService, LibraryLesson, Lesson, SectionWithLessons } from '../types';
import './LibraryPickerModal.css';

interface LibraryPickerModalProps {
  sections: SectionWithLessons[];
  defaultSectionId: number | null;
  getLibraryLessons: NonNullable<CourseSectionsService['getLibraryLessons']>;
  isSaving: boolean;
  onAdd: (lessons: Lesson[]) => Promise<void>;
  onClose: () => void;
}

type TypeFilter = 'all' | 'document' | 'mp4' | 'scorm' | 'url' | 'ILTS';

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'document', label: 'Document' },
  { key: 'mp4', label: 'Video' },
  { key: 'scorm', label: 'SCORM' },
  { key: 'url', label: 'URL' },
  { key: 'ILTS', label: 'ILT' },
];

function typeIcon(lesson: LibraryLesson) {
  if (lesson.lessonType === 'ILTS') return <Users size={18} />;
  switch (lesson.contentType) {
    case 'mp4': return <Video size={18} />;
    case 'scorm': return <Package size={18} />;
    case 'url': return <Link2 size={18} />;
    default: return <FileText size={18} />;
  }
}

function typeLabel(lesson: LibraryLesson): string {
  if (lesson.lessonType === 'ILTS') return 'ILT';
  switch (lesson.contentType) {
    case 'mp4': return 'Video';
    case 'scorm': return 'SCORM';
    case 'url': return 'URL';
    case 'document': return 'Document';
    default: return 'Content';
  }
}

/** Map a library (standalone) lesson to a course-section lesson payload (reference copy). */
function mapLibraryLessonToLesson(lib: LibraryLesson, sectionId: number, sectionTitle: string): Lesson {
  const lesson: Lesson = {
    title: lib.title,
    section: sectionTitle,
    sectionId,
    lessonType: lib.lessonType,
    contentType: lib.contentType ?? undefined,
    lessonDuration: lib.lessonDuration ?? undefined,
    description: lib.description ?? undefined,
  };

  if (lib.lessonType === 'Content-Based') {
    switch (lib.contentType) {
      case 'document': lesson.lessonContentDocument = lib.lessonContentDocument ?? undefined; break;
      case 'mp4': lesson.videoUpload = lib.lessonContentMp4 ?? undefined; break;
      case 'scorm': lesson.scormPackage = lib.lessonContentScorm ?? undefined; break;
      case 'url': lesson.contentUrl = lib.lessonContentUrl ?? undefined; break;
    }
  } else if (lib.lessonType === 'ILTS' && lib.ilts) {
    lesson.iltsType = lib.ilts.mode;
    lesson.meetUrl = lib.ilts.meetUrl ?? undefined;
    lesson.eventVenue = lib.ilts.venue ?? undefined;
    lesson.startDate = lib.ilts.startDate ?? undefined;
    lesson.startTime = lib.ilts.startTime ?? undefined;
    lesson.endDate = lib.ilts.endDate ?? undefined;
    lesson.endTime = lib.ilts.endTime ?? undefined;
  }

  return lesson;
}

const LibraryPickerModal: React.FC<LibraryPickerModalProps> = ({
  sections,
  defaultSectionId,
  getLibraryLessons,
  isSaving,
  onAdd,
  onClose,
}) => {
  const [library, setLibrary] = useState<LibraryLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [targetSectionId, setTargetSectionId] = useState<number | null>(
    defaultSectionId ?? sections[0]?.id ?? null
  );

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const res = await getLibraryLessons();
      if (!active) return;
      if (res.success && res.data) {
        setLibrary(res.data);
      } else {
        setError(res.error?.message || 'Failed to load library lessons');
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [getLibraryLessons]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return library.filter((l) => {
      if (typeFilter !== 'all') {
        if (typeFilter === 'ILTS' ? l.lessonType !== 'ILTS' : l.contentType !== typeFilter) return false;
      }
      if (q && !`${l.title} ${l.description ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [library, search, typeFilter]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (!targetSectionId || selected.size === 0) return;
    const section = sections.find((s) => s.id === targetSectionId);
    const sectionTitle = section?.title ?? '';
    const lessons = library
      .filter((l) => selected.has(l.id))
      .map((l) => mapLibraryLessonToLesson(l, targetSectionId, sectionTitle));
    await onAdd(lessons);
  };

  return (
    <div className="lib-picker-overlay" onClick={onClose}>
      <div className="lib-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lib-picker-header">
          <div className="lib-picker-title">
            <Library size={20} />
            <div>
              <h2>Add from Content Library</h2>
              <p>Pick existing lessons and add them to a section.</p>
            </div>
          </div>
          <button className="lib-picker-close" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>

        <div className="lib-picker-controls">
          <label className="lib-picker-section-select">
            <span>Add to section</span>
            <select
              value={targetSectionId ?? ''}
              onChange={(e) => setTargetSectionId(e.target.value ? Number(e.target.value) : null)}
            >
              {sections.length === 0 && <option value="">No sections</option>}
              {sections.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </label>

          <div className="lib-picker-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search library…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="lib-picker-filters">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`lib-picker-chip ${typeFilter === f.key ? 'active' : ''}`}
                onClick={() => setTypeFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="lib-picker-list">
          {loading ? (
            <div className="lib-picker-empty"><Loader className="lib-picker-spin" size={22} /> Loading library…</div>
          ) : error ? (
            <div className="lib-picker-empty lib-picker-error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="lib-picker-empty">No matching library lessons.</div>
          ) : (
            filtered.map((l) => {
              const checked = selected.has(l.id);
              return (
                <div
                  key={l.id}
                  className={`lib-picker-item ${checked ? 'selected' : ''}`}
                  onClick={() => toggle(l.id)}
                >
                  <div className={`lib-picker-check ${checked ? 'on' : ''}`}>{checked && <Check size={14} />}</div>
                  <div className="lib-picker-item-icon">{typeIcon(l)}</div>
                  <div className="lib-picker-item-main">
                    <span className="lib-picker-item-title" title={l.title}>{l.title}</span>
                    {l.description && <span className="lib-picker-item-desc">{l.description}</span>}
                  </div>
                  <span className="lib-picker-badge">{typeLabel(l)}</span>
                  {l.lessonDuration && <span className="lib-picker-duration">{l.lessonDuration}</span>}
                </div>
              );
            })
          )}
        </div>

        <div className="lib-picker-footer">
          <span className="lib-picker-count">{selected.size} selected</span>
          <div className="lib-picker-actions">
            <button className="lib-picker-btn cancel" onClick={onClose}>Cancel</button>
            <button
              className="lib-picker-btn add"
              disabled={selected.size === 0 || !targetSectionId || isSaving}
              onClick={handleAdd}
            >
              {isSaving ? 'Adding…' : `Add ${selected.size || ''} to section`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibraryPickerModal;
